import React, { useMemo, useEffect } from "react";
import { Box } from "@chakra-ui/react";
import {
  VStack,
  colors,
  StepIndicator,
  HStack,
  Button,
  customEvent,
  Image,
  baseColors,
  pixelCustomEvent,
  PixelEventName,
} from "goi_common";
import { useRouter } from "next/router";
import { useSetupRegistrationContext } from "@/hooks/useSetupRegistrationContext";
import { FomoInlineBanner } from "@/components/Fomo/FomoInlineBanner";
import { sessionStorage, storageKeys } from "@/utils/storage";
import { PRODUCT_INTRO_STEPS } from "@/constants/introSteps";

const ProductIntroContainer = () => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const { productGroup, fnk, isPayCoupon } = router.query;

  useSetupRegistrationContext();

  const steps = PRODUCT_INTRO_STEPS;
  const currentStep = steps[currentIndex];
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;

  const fnkValue = useMemo(() => {
    if (fnk) return fnk;
    const storedPlatform = sessionStorage.get(storageKeys.Platform);
    return storedPlatform === "toss" ? "toss" : undefined;
  }, [fnk]);

  const isPayCouponValue = useMemo(() => {
    if (isPayCoupon) return isPayCoupon;
    return sessionStorage.get(storageKeys.isPayCoupon);
  }, [isPayCoupon]);

  useEffect(() => {
    if (fnkValue === "toss") {
      sessionStorage.set(storageKeys.Platform, "toss");
    }
    if (isPayCouponValue === "true") {
      sessionStorage.set(storageKeys.isPayCoupon, "true");
    }
  }, [fnkValue, isPayCouponValue]);

  useEffect(() => {
    pixelCustomEvent(PixelEventName.ViewContent);
  }, []);

  const getNextPath = () => {
    const params = new URLSearchParams();
    if (productGroup) params.set("productGroup", String(productGroup));
    if (fnkValue) params.set("fnk", String(fnkValue));
    if (isPayCouponValue === "true") params.set("isPayCoupon", "true");
    const queryString = params.toString();
    return `/registration/verification${queryString ? `?${queryString}` : ""}`;
  };

  const handleCta = () => {
    customEvent({
      newGtm: {
        path: "registration",
        trigger_and_target: "product_intro_cta",
      },
    });
    router.push(getNextPath());
  };

  const handleNext = () => {
    if (isLastStep) {
      handleCta();
    } else {
      customEvent({
        newGtm: {
          path: "registration",
          trigger_and_target: `product_intro_step-${currentIndex + 1}`,
        },
      });
      setCurrentIndex((prev) => prev + 1);
    }
  };

  return (
    <>
      <FomoInlineBanner />
      <VStack
        gap={32}
        width="100%"
        alignItems="center"
        padding="32px 20px 108px"
      >
        <StepIndicator
          totalSteps={steps.length}
          currentStep={currentIndex + 1}
        />
        <VStack
          gap="16px"
          width="100%"
          alignItems="center"
          style={{ whiteSpace: "pre-wrap", textAlign: "center" }}
        >
          <Box className="head_24_sb" color={baseColors.label_normal}>
            {currentStep.title}
          </Box>
          <Box
            className="body_1_normal_r"
            color={baseColors.label_alternative_1}
          >
            {currentStep.description}
          </Box>
        </VStack>

        <Image
          src={currentStep.imageSrc}
          alt={currentStep.imageAlt}
          width="320px"
          height="auto"
        />
        {currentStep.extraContent}

        <HStack
          gap={12}
          css={{
            width: "100%",
            position: "fixed",
            padding: "16px 0 24px 0",
            bottom: 0,
            backgroundColor: colors.white,
            justifyContent: "center",
          }}
        >
          <VStack
            gap={12}
            css={{
              width: "100%",
              maxWidth: "600px",
              padding: "0 20px",
            }}
          >
            <HStack
              gap={12}
              css={{
                width: "100%",
              }}
            >
              {!isFirstStep && (
                <Button
                  onClick={() => setCurrentIndex((prev) => prev - 1)}
                  className="subtitle_16_sb"
                  css={{
                    flex: 1,
                    padding: "16px",
                    borderRadius: "8px",
                    background: colors.white,
                    color: colors.gray700,
                    border: `1px solid ${colors.gray200}`,
                  }}
                >
                  이전
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="subtitle_16_sb"
                css={{
                  flex: 1,
                  padding: "16px",
                  borderRadius: "8px",
                  background: colors.orange650,
                  color: colors.white,
                }}
              >
                {isLastStep ? "장례 준비 시작하기" : "다음"}
              </Button>
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </>
  );
};

export default ProductIntroContainer;
